// components/Dropdown.js
import React, { useState, useEffect, useRef, useContext } from 'react';
import { ThemeContext } from "../ThemeContext";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const Dropdown = ({ options, selectedOption, onOptionChange, className = '', scrollable = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const dropdownRef = useRef(null);
  const { isDarkMode } = useContext(ThemeContext);

  // Hover colors: Light mode: #06bae4, Dark mode: #ff9e42
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';

  // Convert hex to rgba for shadows
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Check if full width is requested
  const isFullWidth = className.includes('w-full');
  const baseClassName = isFullWidth ? 'block w-full' : 'inline-block';
  // Clean className by removing w-full if present (already handled by baseClassName)
  const cleanClassName = className.replace(/w-full\s?/g, '').trim();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOptionClick = (option) => {
    onOptionChange(option);
    setIsOpen(false);
  };

  const displayOption = (option) => {
    return option === 'Sentence Completion' ? 'Vocabulary In Context' : option;
  };

  return (
    <div 
      ref={dropdownRef} 
      className={`relative ${baseClassName} ${cleanClassName}`}
      style={{ position: 'relative', zIndex: isOpen ? 10000 : 'auto' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.button
        onClick={() => setIsOpen(prev => !prev)}
        whileHover={{ scale: isFullWidth ? 1 : 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`group flex items-center justify-between gap-2 font-zonaProRegular font-bold text-gray-700 dark:text-gray-300 backdrop-blur-xl bg-transparent dark:bg-transparent hover:bg-white/90 dark:hover:bg-gray-700/90 px-4 py-3 rounded-lg border transition-all duration-200 shadow-md w-full text-left ${
          isFullWidth ? '' : 'min-w-[120px] sm:min-w-[140px] md:min-w-[160px] lg:min-w-[180px]'
        }`}
        style={{
          borderColor: isHovered || isOpen
            ? hexToRgba(hoverColor, 0.4)
            : isDarkMode ? 'rgba(75, 85, 99, 0.6)' : 'rgba(209, 213, 219, 0.6)',
          boxShadow: isHovered || isOpen
            ? `0 4px 6px rgba(0, 0, 0, 0.1), 0 0 0 2px ${hexToRgba(hoverColor, 0.2)}, 0 0 15px ${hexToRgba(hoverColor, 0.3)}`
            : '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <span className="truncate text-gray-800 dark:text-gray-200">{displayOption(selectedOption)}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0"
        >
          <ChevronDown 
            size={18} 
            style={{ color: isHovered || isOpen ? hoverColor : (isDarkMode ? '#9ca3af' : '#6b7280') }}
          />
        </motion.div>
      </motion.button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.ul
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={`absolute top-full left-0 mt-2 backdrop-blur-xl bg-white/95 dark:bg-gray-800/95 rounded-xl shadow-2xl border list-none p-2 w-full ${
              scrollable ? 'max-h-[200px] overflow-y-auto' : ''
            }`}
            style={{
              zIndex: 10000,
              borderColor: isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
              boxShadow: `0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 0 0 1px ${hexToRgba(hoverColor, 0.1)}, 0 0 20px ${hexToRgba(hoverColor, 0.2)}`,
            }}
          >
            {options.map((option, index) => (
              <motion.li
                key={index}
                onClick={() => handleOptionClick(option)}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.02 }}
                whileHover={{ 
                  scale: 1.02,
                  x: 4,
                }}
                className="px-4 py-2 cursor-pointer font-zonaProRegular text-sm sm:text-base rounded-lg transition-all duration-150 text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-700/50"
                style={{
                  color: isDarkMode ? '#e5e7eb' : '#374151',
                }}
              >
                {displayOption(option)}
              </motion.li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dropdown;
